import { ErrorMessage, Field, Formik, FormikHelpers } from "formik";
import React, { useCallback, useRef, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import ReCAPTCHA from "react-google-recaptcha";
import { apiConfiguration, ReCAPTCHASiteKey } from "../../api-configuration";
import { DefaultEditor } from "../../components/editor";
import { AssetUpload } from "../../components/forms/asset-upload";
import { EmailSubscribeForm } from "../../components/forms/email-subscribe-form";
import { ErrorsListCard } from "../../components/forms/errors-list";
import { NumberFormGroup } from "../../components/forms/groups/number-field-group";
import { TextFieldFormGroup } from "../../components/forms/groups/text-field-group";
import { SaveData } from "../../components/forms/save-data";
import { logError } from "../../errors";
import {
    ContentApi,
    UserArticleAuthorType,
    UserArticleForm,
} from "../../lib-api";
import { SubmitState } from "../../util/loading";
import { getObject } from "../../util/local-storage";
import { validationSchema } from "./validation";

interface StorageModel {
    values: UserArticleForm;
    state: FormState;
}

interface FormState {
    errors?: { [key: string]: string };
    state: SubmitState;
}

const api = new ContentApi(apiConfiguration);
const storageKey = "user-article-draft";

function getInitialValues(savedValues?: UserArticleForm) {
    return {
        headline: savedValues?.headline ?? "",
        body: savedValues?.body ?? "",
        assets: savedValues?.assets ?? [],
        email: savedValues?.email ?? "",
        name: savedValues?.name ?? "",
        rightsToAssets: savedValues?.rightsToAssets ?? false,
        type: savedValues?.type ?? UserArticleAuthorType.PrivatePerson,
        companyName: savedValues?.companyName ?? "",
        cvr: savedValues?.cvr ?? undefined,
        facebookPageUrl: savedValues?.facebookPageUrl ?? "",
        recaptcha: "",
        associationName: "",
    };
}

function getInitialState(savedState?: FormState): FormState {
    return {
        errors: savedState?.errors ?? undefined,
        state: savedState?.state ?? SubmitState.None,
    };
}

export const UserArticle: React.FC<{ csrf: string }> = ({ csrf }) => {
    const [savedData] = useState(getObject<StorageModel>(storageKey));
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [formState, setFormState] = useState<FormState>(
        getInitialState(savedData?.state)
    );
    const recaptchaRef = useRef<ReCAPTCHA>();

    const handleSubmit = useCallback(
        async (
            values: UserArticleForm,
            helpers: FormikHelpers<UserArticleForm>
        ) => {
            try {
                setFormState({
                    state: SubmitState.None,
                });
                await api
                    .withMiddleware({
                        pre: async (context) => ({
                            ...context,
                            init: {
                                ...context.init,
                                headers: {
                                    ...context.init.headers,
                                    RequestVerificationToken: csrf,
                                },
                            },
                        }),
                    })
                    .postUserArticle({ userArticleForm: values });

                setFormState({
                    state: SubmitState.Submitted,
                });
            } catch (e) {
                if (e instanceof Response && e.status == 400) {
                    const json = await e.json();

                    e = new Error(JSON.stringify(json));

                    setFormState({
                        state: SubmitState.Error,
                        errors: json.errors,
                    });
                } else {
                    setFormState({
                        state: SubmitState.Error,
                    });
                }

                if (!(e instanceof Error)) {
                    e = new Error(JSON.stringify(e));
                }
                logError(e);
                recaptchaRef.current?.reset();
                helpers.setFieldValue("recaptcha", undefined);
            }
        },
        [recaptchaRef.current, setFormState]
    );

    return (
        <Formik<UserArticleForm>
            initialValues={getInitialValues(savedData?.values)}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
        >
            {({
                handleChange,
                values,
                touched,
                errors,
                isSubmitting,
                setFieldValue,
                handleSubmit,
                resetForm,
            }) => (
                <>
                    <SaveData<StorageModel>
                        data={{ values, state: formState }}
                        storageKey={storageKey}
                    />
                    {formState.state === SubmitState.Submitted ? (
                        <>
                            <h1>Tak for din artikel</h1>
                            <p>
                                Du modtager besked, når vi publicerer eller har spørgsmål til
                                artiklen
                            </p>
                            <Button
                                onClick={() => {
                                    resetForm({ values: getInitialValues() });
                                    setFormState(getInitialState());
                                }}
                                variant="primary"
                            >
                                Start ny artikel
                            </Button>
                            <Row className="py-5">
                                <Col xs={12} sm={6} md={4}>
                                    <h5>Er du tilmeldt vores nyhedsbrev?</h5>
                                    <EmailSubscribeForm
                                        location="Læserbidrag"
                                        hasAutofocus={false}
                                    />
                                </Col>
                            </Row>
                        </>
                    ) : (
                        <>
                            <Form onSubmit={handleSubmit}>
                                <h1>Opret artikel</h1>
                                <hr />
                                <Form.Group>
                                    <div>
                                        <Form.Label>Hvem er du?</Form.Label>
                                    </div>
                                    <div className="d-flex">
                                        <div
                                            className={`flex-grow-1 text-center py-3 cursor-pointer border border-right-0 ${values.type === UserArticleAuthorType.PrivatePerson
                                                    ? "bg-light"
                                                    : ""
                                                }`}
                                            onClick={() =>
                                                setFieldValue(
                                                    "type",
                                                    UserArticleAuthorType.PrivatePerson
                                                )
                                            }
                                        >
                                            <Field
                                                name="type"
                                                value={UserArticleAuthorType.PrivatePerson}
                                                type="radio"
                                                as={(props) => (
                                                    <Form.Check label="Privatperson" {...props} />
                                                )}
                                            />
                                        </div>
                                        <div
                                            className={`flex-grow-1 text-center py-3 cursor-pointer border ${values.type === UserArticleAuthorType.Company
                                                    ? "bg-light"
                                                    : ""
                                                }`}
                                            onClick={() =>
                                                setFieldValue("type", UserArticleAuthorType.Company)
                                            }
                                        >
                                            <Field
                                                name="type"
                                                value={UserArticleAuthorType.Company}
                                                type="radio"
                                                className="cursor-pointer"
                                                as={(props) => (
                                                    <Form.Check label="Virksomhed" {...props} />
                                                )}
                                            />
                                        </div>
                                        <div
                                            className={`flex-grow-1 text-center py-3 cursor-pointer border border-left-0 ${values.type === UserArticleAuthorType.Association
                                                    ? "bg-light"
                                                    : ""
                                                }`}
                                            onClick={() =>
                                                setFieldValue("type", UserArticleAuthorType.Association)
                                            }
                                        >
                                            <Field
                                                name="type"
                                                value={UserArticleAuthorType.Association}
                                                type="radio"
                                                className="cursor-pointer"
                                                as={(props) => (
                                                    <Form.Check label="Forening" {...props} />
                                                )}
                                            />
                                        </div>
                                    </div>
                                </Form.Group>
                                {values.type && (
                                    <>
                                        <TextFieldFormGroup
                                            fieldName="headline"
                                            fieldDisplayName="Overskrift *"
                                            placeholder="Overskrift..."
                                            error={touched.headline ? errors.headline : undefined}
                                            value={values.headline}
                                            disabled={isSubmitting}
                                            handleChange={handleChange}
                                        />
                                        <Form.Group>
                                            <DefaultEditor
                                                onChange={(value) => setFieldValue("body", value, true)}
                                                value={values.body}
                                                />
                                                <p className="pt-2">Vi minder om, at vi bringer dit læserbidrag 1-til-1, som vi modtager det, samt at du står til fulde ansvar for holdninger og teksten heri.</p>
                                            <ErrorMessage
                                                name="body"
                                                component={Form.Text}
                                                className="text-danger"
                                            />
                                        </Form.Group>
                                        <div className="row">
                                            <TextFieldFormGroup
                                                fieldName="name"
                                                className="col-6"
                                                fieldDisplayName="Dit navn *"
                                                placeholder="Dit navn..."
                                                error={touched.name ? errors.name : undefined}
                                                value={values.name}
                                                disabled={isSubmitting}
                                                handleChange={handleChange}
                                            />
                                            <TextFieldFormGroup
                                                fieldName="email"
                                                className="col-6"
                                                fieldDisplayName="Email *"
                                                placeholder="Din email..."
                                                error={touched.email ? errors.email : undefined}
                                                value={values.email}
                                                disabled={isSubmitting}
                                                handleChange={handleChange}
                                            />
                                        </div>

                                        {[
                                            UserArticleAuthorType.Company,
                                            UserArticleAuthorType.Association,
                                        ].includes(values.type) && (
                                                <TextFieldFormGroup
                                                    fieldName="facebookPageUrl"
                                                    fieldDisplayName="Facebook side (hvis du ønsker at blive tagget i facebookopslaget)"
                                                    placeholder="f.eks. https://www.facebook.com/din-facebook-side"
                                                    error={
                                                        touched.facebookPageUrl
                                                            ? errors.facebookPageUrl
                                                            : undefined
                                                    }
                                                    value={values.facebookPageUrl ?? ""}
                                                    disabled={isSubmitting}
                                                    handleChange={handleChange}
                                                />
                                            )}
                                        {values.type === UserArticleAuthorType.Company && (
                                            <div className="row py-0">
                                                <TextFieldFormGroup
                                                    className="col-6"
                                                    fieldName="companyName"
                                                    fieldDisplayName="Firmanavn *"
                                                    placeholder="Dit firmanavn..."
                                                    error={
                                                        touched.companyName ? errors.companyName : undefined
                                                    }
                                                    value={values.companyName ?? ""}
                                                    disabled={isSubmitting}
                                                    handleChange={handleChange}
                                                />
                                                <NumberFormGroup
                                                    className="col-6"
                                                    fieldName="cvr"
                                                    fieldDisplayName="CVR *"
                                                    placeholder="Dit CVR nummer..."
                                                    error={touched.cvr ? errors.cvr : undefined}
                                                    value={values.cvr ?? undefined}
                                                    disabled={isSubmitting}
                                                    handleChange={handleChange}
                                                />
                                            </div>
                                        )}
                                        {values.type === UserArticleAuthorType.Association && (
                                            <TextFieldFormGroup
                                                fieldName="associationName"
                                                fieldDisplayName="Foreningsnavn *"
                                                placeholder="Dit foreningsnavn..."
                                                error={
                                                    touched.associationName
                                                        ? errors.associationName
                                                        : undefined
                                                }
                                                value={values.associationName ?? ""}
                                                disabled={isSubmitting}
                                                handleChange={handleChange}
                                            />
                                        )}

                                        <AssetUpload csrf={csrf} maxAssets={4} />
                                        <Form.Group>
                                            <Form.Check
                                                type="checkbox"
                                                name="rightsToAssets"
                                                checked={values.rightsToAssets}
                                                onChange={handleChange}
                                                label="Jeg bekræfter, at jeg har brugsret til billederne"
                                            />
                                            <ErrorMessage
                                                name="rightsToAssets"
                                                component={Form.Text}
                                                className="text-danger"
                                            />
                                        </Form.Group>
                                        <Form.Group>
                                            <ReCAPTCHA
                                                sitekey={ReCAPTCHASiteKey}
                                                onChange={(token) => setFieldValue("recaptcha", token)}
                                                ref={recaptchaRef}
                                            />
                                        </Form.Group>
                                        <Button
                                            className="align-self-end mt-3"
                                            disabled={isSubmitting}
                                            variant="primary"
                                            block={false}
                                            type="submit"
                                            size="lg"
                                            onClick={() => setSubmitAttempted(true)}
                                        >
                                            Indsend til redaktionen
                                        </Button>
                                    </>
                                )}
                            </Form>
                            {submitAttempted && <ErrorsListCard errors={errors} />}
                        </>
                    )}
                    {formState.state == SubmitState.Error && (
                        <div>
                            {formState.errors ? (
                                <ErrorsListCard
                                    title="Vores server meldte følgende fejl"
                                    errors={formState.errors}
                                />
                            ) : (
                                <>Ups, der opstod en fejl. Prøv evt. at genindlæse siden.</>
                            )}
                        </div>
                    )}
                </>
            )}
        </Formik>
    );
};
